import { useSnapshot } from 'valtio'
import { subscribeKey } from 'valtio/utils'
import { useMemo, useEffect, useState, useRef } from 'react'
import { appQueryParams, updateQsParam } from '../appParams'
import { activeModalStack, miscUiState, openOptionsMenu, hideCurrentModal } from '../globalState'
import { quickDevReconnect } from './AppStatusProvider'
import { onChangeButtonState, packetsReplayState } from './state/packetsReplayState'
import ReplayPanel from './ReplayPanel'
import PlayerBottomBar from './components/replay/PlayerBottomBar'
import InfoViewPanel from './components/replay/InfoViewPanel'
import TelemetryPanel from './components/replay/TelemetryPanel'
import TopMenu from './components/replay/TopMenu'
import MobileReplayMenu from './components/replay/MobileReplayMenu'
import ReplayHudPanel from './components/replay/ReplayHudPanel'
import HoloSignPortal from './HoloSignPortal'

const EMPTY_PACKETS: any[] = []
const MOBILE_REPLAY_QUERY = '(max-width: 720px), (max-height: 560px) and (pointer: coarse)'

export default function PacketsReplayProvider () {
  const state = useSnapshot(packetsReplayState)
  const { gameLoaded } = useSnapshot(miscUiState)
  const activeModals = useSnapshot(activeModalStack)
  const replaySettingsOpen = activeModals.some(modal => modal.reactType === 'options-replay')
  const forceMobileReplayUi = appQueryParams.mobileReplayUi === '1'
  const [mobileReplayUi, setMobileReplayUi] = useState(() => (
    forceMobileReplayUi || (typeof window !== 'undefined' && window.matchMedia(MOBILE_REPLAY_QUERY).matches)
  ))
  useEffect(() => {
    const media = window.matchMedia(MOBILE_REPLAY_QUERY)
    const update = () => setMobileReplayUi(forceMobileReplayUi || media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [forceMobileReplayUi])
  useEffect(() => {
    if (mobileReplayUi && packetsReplayState.showPacketList) {
      packetsReplayState.showPacketList = false
    }
  }, [mobileReplayUi])
  const [perspective, setPerspective] = useState<'first_person' | 'third_person_back' | 'third_person_front'>('first_person')
  useEffect(() => {
    let frame = 0
    let unsubscribe: (() => void) | undefined
    const connect = () => {
      const reactive = (globalThis as any).appViewer?.playerState?.reactive
      if (!reactive) {
        frame = requestAnimationFrame(connect)
        return
      }
      setPerspective(reactive.perspective)
      unsubscribe = subscribeKey(reactive, 'perspective', setPerspective)
    }
    connect()
    return () => {
      if (frame) cancelAnimationFrame(frame)
      unsubscribe?.()
    }
  }, [gameLoaded])
  const holoEnabled = perspective === 'first_person' && !mobileReplayUi

  // ── Swipe gestures (mobile only) ─────────────────────────────────────
  // Swipe down (anywhere) → open settings; swipe up → close settings / show controls.
  useEffect(() => {
    if (!mobileReplayUi) return
    let startY = 0
    let startX = 0
    const SWIPE_THRESHOLD = 60
    const MAX_DRIFT = 40 // max horizontal drift to still count as vertical swipe

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      startY = e.touches[0].clientY
      startX = e.touches[0].clientX
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (!startY) return
      const endY = e.changedTouches[0].clientY
      const endX = e.changedTouches[0].clientX
      const deltaY = endY - startY
      const deltaX = Math.abs(endX - startX)
      startY = 0

      // must be primarily vertical and exceed threshold
      if (deltaX > MAX_DRIFT || Math.abs(deltaY) < SWIPE_THRESHOLD) return
      // ignore if interacting with a UI element (settings, bar, panel)
      const target = e.target as HTMLElement
      if (target.closest('[data-replay-ui]') || target.closest('.screen-content') || target.closest('input[type="range"]')) return

      const settingsOpen = activeModalStack.length > 0
      if (deltaY > 0 && !settingsOpen) {
        // swipe down → open settings
        openOptionsMenu('replay')
      } else if (deltaY < 0 && settingsOpen) {
        // swipe up → close settings
        hideCurrentModal()
      }
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }, [mobileReplayUi])

  const [uiProgress, setUiProgress] = useState({ current: 0, total: 0 })
  const [uiCurrentTimeMs, setUiCurrentTimeMs] = useState(0)

  const seekToTime = (timeMs: number, fallbackPacketIdx?: number) => {
    const totalMs = packetsReplayState.totalTimeMs
    if (!totalMs) return
    const total = packetsReplayState.progress.total
    let index: number
    const packetTimesMs = packetsReplayState.packetTimesMs
    if (packetTimesMs.length) {
      let low = 0
      let high = packetTimesMs.length - 1
      while (low < high) {
        const mid = Math.floor((low + high) / 2)
        if ((packetTimesMs[mid] ?? 0) < timeMs) {
          low = mid + 1
        } else {
          high = mid
        }
      }
      index = low
    } else if (total > 0) {
      index = Math.round((timeMs / totalMs) * total)
    } else if (fallbackPacketIdx !== undefined) {
      index = fallbackPacketIdx
    } else {
      return
    }
    const clamped = Math.max(0, Math.min(total, index))
    if (total > 0 && clamped < packetsReplayState.progress.current) {
      packetsReplayState.internalRewindTo = clamped
    } else {
      packetsReplayState.seekTo = clamped
    }
  }

  useEffect(() => {
    const getTimeForIndex = (index: number) => {
      const totalTimeMs = packetsReplayState.totalTimeMs
      const progress = packetsReplayState.progress
      if (!totalTimeMs || progress.total <= 0) return 0
      const fallback = (index / progress.total) * totalTimeMs
      const packetTimesMs = packetsReplayState.packetTimesMs
      if (!packetTimesMs.length) return fallback
      const clamped = Math.max(0, Math.min(packetTimesMs.length - 1, Math.round(index)))
      return packetTimesMs[clamped] ?? fallback
    }

    const interval = setInterval(() => {
      const next = packetsReplayState.progress
      setUiProgress((prev) => {
        if (prev.current === next.current && prev.total === next.total) return prev
        return { current: next.current, total: next.total }
      })

      const g = globalThis as any
      const totalTimeMs = packetsReplayState.totalTimeMs
      const packetTime = typeof g.__replayPacketTimeMs === 'number' ? g.__replayPacketTimeMs : undefined
      const packetWallTime = typeof g.__replayPacketWallTimeMs === 'number' ? g.__replayPacketWallTimeMs : undefined
      let nextTimeMs = getTimeForIndex(next.current)

      if (
        packetsReplayState.isPlaying &&
        !g.__replayPacketSeeking &&
        typeof packetTime === 'number'
      ) {
        const replaySpeed = typeof g.__replayPacketSpeed === 'number'
          ? Math.max(g.__replayPacketSpeed, 0.001)
          : Math.max(packetsReplayState.speed || 1, 0.001)
        const wallElapsed = typeof packetWallTime === 'number' ? Math.max(0, performance.now() - packetWallTime) : 0
        nextTimeMs = packetTime + wallElapsed * replaySpeed
      }

      if (totalTimeMs) {
        nextTimeMs = Math.max(0, Math.min(totalTimeMs, nextTimeMs))
      }
      setUiCurrentTimeMs((prev) => {
        return Math.abs(prev - nextTimeMs) < 20 ? prev : nextTimeMs
      })
    }, 50)

    return () => {
      clearInterval(interval)
    }
  }, [])

  const autocomplete = useMemo(() => {
    if (!loadedData) return
    return {
      client: Object.keys(loadedData.protocol.play.toClient.types).filter(a => a.startsWith('packet_')).map(a => a.slice('packet_'.length)),
      server: Object.keys(loadedData.protocol.play.toServer.types).filter(a => a.startsWith('packet_')).map(a => a.slice('packet_'.length))
    }
  }, [gameLoaded])

  // First person uses CSS3D signs. Third person falls back to the original DOM
  // panels so the player model and orbit camera never intersect HUD geometry.
  useEffect(() => {
    if (!state.isOpen) return
    let raf = 0
    let applied = false
    const apply = () => {
      const backend = (globalThis as any).appViewer?.backend
      if (backend?.id !== 'threejs' || !backend.backendMethods) return false
      backend.backendMethods.setHoloBillboardVisible?.(holoEnabled)
      applied = true
      return true
    }
    const tick = () => { if (!apply()) raf = requestAnimationFrame(tick) }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      if (applied && holoEnabled) {
        const backend = (globalThis as any).appViewer?.backend
        backend?.backendMethods?.setHoloBillboardVisible?.(false)
      }
    }
  }, [state.isOpen, holoEnabled])

  if (!state.isOpen) return null

  const mockEventsActive = appQueryParams.mockEvents === '1'
  const demoEvents: typeof state.replayEvents = mockEventsActive && state.totalTimeMs ? [
    { type: 'chat',             time: state.totalTimeMs * 0.08,  packetIndex: 0,  label: 'привет всем!' },
    { type: 'chat',             time: state.totalTimeMs * 0.15,  packetIndex: 1,  label: 'хай' },
    { type: 'high_activity',    time: state.totalTimeMs * 0.22,  packetIndex: 2,  label: 'Активность' },
    { type: 'damage',           time: state.totalTimeMs * 0.27,  packetIndex: 3,  label: 'Урон (3 ❤)' },
    { type: 'damage',           time: state.totalTimeMs * 0.31,  packetIndex: 4,  label: 'Урон (2 ❤)' },
    { type: 'death',            time: state.totalTimeMs * 0.38,  packetIndex: 5,  label: 'Смерть' },
    { type: 'respawn',          time: state.totalTimeMs * 0.42,  packetIndex: 6,  label: 'Возрождение' },
    { type: 'afk',              time: state.totalTimeMs * 0.51,  packetIndex: 7,  label: 'AFK: Player1' },
    { type: 'afk_end',          time: state.totalTimeMs * 0.58,  packetIndex: 8,  label: 'Вернулся из AFK' },
    { type: 'teleport',         time: state.totalTimeMs * 0.65,  packetIndex: 9,  label: 'Телепорт' },
    { type: 'dimension_change', time: state.totalTimeMs * 0.73,  packetIndex: 10, label: 'Нижний мир' },
    { type: 'high_activity',    time: state.totalTimeMs * 0.82,  packetIndex: 11, label: 'Активность' },
    { type: 'chat',             time: state.totalTimeMs * 0.91,  packetIndex: 12, label: 'gg!' },
  ] as any[] : []

  const packets = state.showPacketList ? state.packetsPlayback : EMPTY_PACKETS
  const activeEvents = mockEventsActive && demoEvents.length ? demoEvents : state.replayEvents

  const handleRestart = () => {
    packetsReplayState.isPlaying = false
    packetsReplayState.seekTo = null
    packetsReplayState.internalRewindTo = 0
  }

  const handleSeek = (index: number) => {
    const clamped = Math.max(0, Math.min(uiProgress.total, index))
    if (clamped < packetsReplayState.progress.current) {
      packetsReplayState.internalRewindTo = clamped
    } else {
      packetsReplayState.seekTo = clamped
    }
  }

  const handleTogglePackets = () => {
    packetsReplayState.isMinimized = false
    packetsReplayState.showPacketList = !packetsReplayState.showPacketList
  }

  return (
    <>
      {/* Info + Telemetry now live as hanging stone storefronts in the shared
         3D scene (same renderer/camera as the world + SkyPortal). The flat DOM
         overlays are portaled into the slab faces instead of sitting on top of
         the canvas. */}
      {!mobileReplayUi && (
        <HoloSignPortal side="left" enabled={holoEnabled}>
          <InfoViewPanel
            replayName={state.replayName}
            isPlaying={state.isPlaying}
            progress={uiProgress}
            totalTimeMs={state.totalTimeMs}
            currentTimeMs={uiCurrentTimeMs}
            packetTimesMs={state.packetTimesMs}
            replayEvents={activeEvents as any[]}
            onSeekToTime={seekToTime}
          />
        </HoloSignPortal>
      )}

      {!mobileReplayUi && (
        <HoloSignPortal side="right" enabled={holoEnabled}>
          <TelemetryPanel />
        </HoloSignPortal>
      )}

      {holoEnabled && (
        <HoloSignPortal side="hud">
          <ReplayHudPanel />
        </HoloSignPortal>
      )}

      {!mobileReplayUi && !state.showPacketList && activeModals.length === 0 && (
        <TopMenu
          showPackets={state.showPacketList}
          onTogglePackets={handleTogglePackets}
          onOpenSettings={() => openOptionsMenu('replay')}
        />
      )}

      {mobileReplayUi && activeModals.length === 0 && (
        <MobileReplayMenu
          perspective={perspective}
          onOpenSettings={() => openOptionsMenu('replay')}
        />
      )}

      {/* Bottom bar stays mounted always — when a modal (settings) is open it
          slides fully below the viewport via modalOpen, leaving only its own
          collapse tab peeking (same tab used for the timeline collapse). */}
      <PlayerBottomBar
        isPlaying={state.isPlaying}
        progress={uiProgress}
        speed={state.speed}
        seekTo={typeof state.seekTo === 'number' ? state.seekTo : null}
        replayEvents={activeEvents as any[]}
        totalTimeMs={state.totalTimeMs}
        currentTimeMs={uiCurrentTimeMs}
        packetTimesMs={state.packetTimesMs}
        mobileLayout={mobileReplayUi}
        modalOpen={replaySettingsOpen}
        onCloseModal={hideCurrentModal}
        onPlayPause={(isPlaying) => { packetsReplayState.isPlaying = isPlaying }}
        onRestart={handleRestart}
        onSpeedChange={(speed) => {
          packetsReplayState.speed = speed
          updateQsParam('replaySpeed', speed === 1 ? undefined : speed.toString())
        }}
        onSeek={handleSeek}
        onSeekToTime={seekToTime}
      />

      {state.showPacketList && (
        <ReplayPanel
          hidePlaybackBar
          replayName={state.replayName}
          packets={packets}
          isPlaying={state.isPlaying}
          progress={uiProgress}
          speed={state.speed}
          replayEvents={activeEvents as any[]}
          totalTimeMs={state.totalTimeMs}
          onSeekToTime={seekToTime}
          defaultFilter={appQueryParams.replayFilter ?? ''}
          clientPacketsAutocomplete={autocomplete?.client ?? []}
          serverPacketsAutocomplete={autocomplete?.server ?? []}
          customButtons={state.customButtons}
          onPlayPause={(isPlaying) => { packetsReplayState.isPlaying = isPlaying }}
          onRestart={handleRestart}
          onSpeedChange={(speed) => {
            packetsReplayState.speed = speed
            updateQsParam('replaySpeed', speed === 1 ? undefined : speed.toString())
          }}
          onFilterChange={(filter) => { updateQsParam('replayFilter', filter) }}
          onCustomButtonToggle={(button) => {
            onChangeButtonState(button as keyof typeof packetsReplayState.customButtons, !state.customButtons[button].state)
          }}
        />
      )}
    </>
  )
}
